import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";

export default function Header({ showBanner = false, homePath = "/" }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isActive = (path) => location.pathname === path;

  const handleNavClick = (e, path) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    
    // If clicking Home or logo, scroll to top
    if (path === homePath) {
      if (location.pathname === homePath) {
        window.scrollTo(0, 0);
      } else {
        navigate(homePath);
        setTimeout(() => window.scrollTo(0, 0), 100);
      }
      return;
    }
    
    // Check if it's an anchor link on the homepage
    if (path.startsWith("/#")) {
      const targetId = path.substring(2);
      
      if (location.pathname === homePath) {
        // Already on homepage, just scroll
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else {
        // Navigate to homepage first, then scroll
        navigate(homePath, { state: { scrollTo: targetId } });
      }
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };

  // Handle scroll after navigation
  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location]);

  const navLinks = [
    { path: homePath, label: "Home" },
    { path: "/#pricing", label: "Pricing" },
    { path: "/#faq", label: "FAQ" },
    { path: "/contact", label: "Contact" }
  ];

  return (
    <header className={`fixed left-0 right-0 z-50 glass border-b border-slate-100 ${showBanner ? 'top-10' : 'top-0'}`} data-testid="header">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link 
            to={homePath}
            className="flex items-center gap-2" 
            data-testid="logo-link"
            onClick={(e) => {
              e.preventDefault();
              if (location.pathname === homePath) {
                window.scrollTo(0, 0);
              } else {
                navigate(homePath);
                setTimeout(() => window.scrollTo(0, 0), 100);
              }
            }}
          >
            <span className="text-xl lg:text-2xl font-bold text-slate-900">Cliptour</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => handleNavClick(e, link.path)}
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  isActive(link.path) 
                    ? "text-slate-900" 
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button 
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-slate-600 hover:text-slate-900"
                    data-testid="login-btn"
                  >
                    Log in
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button 
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6"
                    data-testid="signup-btn"
                  >
                    Sign up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-900" />
            ) : (
              <Menu className="w-6 h-6 text-slate-900" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.path}
                  href={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className={`text-sm font-medium py-2 cursor-pointer ${
                    isActive(link.path) 
                      ? "text-slate-900" 
                      : "text-slate-600"
                  }`}
                >
                  {link.label}
                </a>
              ))}
              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-slate-100">
                {user ? (
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-slate-900 text-white rounded-full">
                      Dashboard
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full rounded-full">
                        Log in
                      </Button>
                    </Link>
                    <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-slate-900 text-white rounded-full">
                        Sign up
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}